import { useCallback, useEffect, useState } from 'react';
import { fetchDashboardMeta, getCachedDashboardMeta, type DashboardMeta } from '../../../lib/dashboard';
import { useDashboardDuplexer } from '../../../lib/use-dashboard-duplexer';
import { useHoldDeadline } from '../../../lib/use-hold-deadline';
import { useHomeContentReady } from '../../../lib/use-home-content-ready';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useOnboardingEntry } from '../../../lib/use-onboarding-entry';
import { useHomepage } from '../../HomepageContext';
import { PageSurface } from '../../PageSurface';
import { CatchUpSections } from '../catch-up';
import { DashboardPane } from './DashboardPane';
import { DashboardFrame } from './DashboardFrame';
import { DashboardSkeleton } from './DashboardSkeleton';
import { EmptyDashboard } from './EmptyDashboard';
import { DashboardLoading } from './DashboardLoading';
import { HomeHeader } from './HomeHeader';

export function DashboardView() {
  const [meta, setMeta] = useState<DashboardMeta | null | undefined>(() => getCachedDashboardMeta());
  const [endpointFailed, setEndpointFailed] = useState(false);
  const [docHeight, setDocHeight] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { state: homepage } = useHomepage();
  // A brand-new user has no dashboard, so the home would paint the "One home for everything" empty-state for
  // the beat before the DERIVED onboarding kickoff opens the chat over it — the exact flicker native
  // suppresses with `onboardingKickoffActive`. Hold the empty-state behind the loader until the onboarding
  // decision resolves: a kickoff/resume user gets the chat instead of ever seeing it; a resolved
  // non-onboarding user (no dashboard) still gets it. Only gates the empty path — a user WITH a dashboard
  // (`meta` present) is unaffected.
  const { route: onboardingRoute, resolved: onboardingResolved } = useOnboardingEntry();
  const onboardingActive = onboardingRoute === 'kickoff' || onboardingRoute === 'resume';
  const homeContentReady = useHomeContentReady();
  // Every hold below is "wait until a read settles", and each read is individually
  // error-inclusive — but only individually. A read that never settles at all (a recipient
  // that 404s forever because the search index never catches up, a query whose observer never
  // mounts) is not an error and never becomes one, so the hold has no lower bound and the
  // loader is the last thing the user ever sees. Support has this as a live report: users on
  // the loop loader for minutes, reloading, with every server read returning 200.
  // So cap it. Past the cap, show what we have — an empty home is a screen the user can act
  // on and a spinner is not, and a late kickoff opening over it costs a flash, not a session.
  const holdExpired = useHoldDeadline(!onboardingResolved || !homeContentReady);
  const emptyHome = onboardingResolved || holdExpired ? (
    <DashboardPane bare>
      <EmptyDashboard />
    </DashboardPane>
  ) : (
    // The onboarding hold is the first thing a new user sees — Symphony's own loop loader, not a bare spinner.
    <DashboardLoading pane />
  );

  // src:198 load — the home dashboard's `fetchDashboardMeta(undefined)` probe. `meta`
  // is undefined while probing; a cache hit seeds it defined on mount, so a cached
  // render emits nothing. `endpointFailed` is the error outcome.
  useLoadBi('dashboard-web', { isLoading: meta === undefined && !endpointFailed, isError: endpointFailed });

  const load = useCallback((force?: boolean) => {
    let cancelled = false;
    fetchDashboardMeta(undefined, { force })
      .then((m) => {
        if (!cancelled) {
          setMeta(m);
          setEndpointFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) setEndpointFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  useDashboardDuplexer(() => {
    load(true);
    setReloadKey((k) => k + 1);
  });

  // A GENUINELY new onboarding user has no dashboard, and the fullscreen chat is their whole screen — so
  // never block their home on the dashboard-existence probe. For a page that only ever resolves to "no
  // dashboard" that probe is pure latency behind the chat — a blank spinner. Render the empty home
  // immediately instead; the chat covers it, and once onboarding finishes (route → none) the normal
  // dashboard flow resumes.
  //
  // But `onboardingActive` is NOT the same as "has no home": a pack-accept user is provisioned a full home +
  // workspace while their recipient is still ONBOARDING_ENGAGED, so they are onboarding-active WITH a built
  // home. Gate on `!meta` so a home that already exists always wins over the pre-onboarding hero — only the
  // empty/probing path (`meta` null or undefined) is held behind the onboarding short-circuit.
  if (onboardingActive && !meta) return emptyHome;

  if (meta === null) return emptyHome;

  // Reached only while probing (meta === undefined): a failed probe with a known
  // homepage state resolves to the page or the empty home; a still-loading homepage
  // falls through to the loader below.
  if (!meta && endpointFailed) {
    if (homepage.status === 'present')
      return (
        <PageSurface
          slug={homepage.index.home ?? ''}
          header={
            <>
              <HomeHeader />
              <CatchUpSections />
            </>
          }
        />
      );
    if (homepage.status === 'absent') return emptyHome;
  }

  // Hold the home reveal behind the FAST, non-dashboard reads only — the onboarding decision plus the brief
  // and workspaces ("workspace and stuff"), every one of them cheap, retried and error-inclusive. This keeps
  // the two behaviours the loader always had: a brand-new user heading into the kickoff chat never flashes
  // the populated home first, and the catch-up chrome appears in one piece instead of popping in section by
  // section. What it deliberately DROPS from the wait is the dashboard: its slow, sometimes-cold render probe
  // no longer holds the whole screen. On re-entry every read is cached, so this resolves instantly.
  if ((!onboardingResolved || !homeContentReady) && !holdExpired) return <DashboardLoading pane />;

  // The dashboard fills in on its own: the pane's catch-up chrome (greeting, brief, tasks, workspaces) is
  // already settled above, and the dashboard REGION carries its own placeholder — a calm shimmer bento —
  // until it resolves. This mirrors the native apps, which show the home while only the dashboard loads.
  //
  // While probing (`meta === undefined`) the dashboard slot is the skeleton; once `meta` arrives the frame
  // mounts in its place and keeps the skeleton over the iframe (its own cover) until the document is ready,
  // so the placeholder is continuous rather than flashing between two indicators.
  return (
    <DashboardPane scroll={meta ? docHeight != null : false}>
      {meta ? (
        <DashboardFrame
          target={{}}
          name={meta.name}
          reloadKey={reloadKey}
          docHeight={docHeight}
          onHeight={setDocHeight}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      ) : (
        <DashboardSkeleton />
      )}
    </DashboardPane>
  );
}
